import * as React from 'react';

import { cn } from '@/design/utils';

/**
 * The platform's own checkbox, in the one shape this product draws it.
 *
 * **Native rather than a rebuilt control**, for the reasons {@link NativeSelect} gives: it is
 * reachable by a screen reader with no `aria` plumbing, it is operated by the space bar without a
 * line of code, and it is the version that survives a browser nobody tested in.
 *
 * It exists as a component because the class string had been copied to sixteen places and one of
 * them had drifted — an invisible drift, since a native checkbox ignores an author's border and
 * background in both engines and only `accent-color` and the box's SIZE reach it. That is exactly
 * the kind of difference nobody finds by looking, which is why the answer is one place rather than a
 * careful sixteen. `accent-primary` is what makes the tick this product's accent in both themes;
 * the border classes are kept because they cost no CSS rule of their own and because removing dead
 * styling is a separate change from centralising it.
 *
 * Layout belongs to the caller: a checkbox beside a two-line label needs its own top margin, and a
 * component that guessed one would be wrong on most of the sixteen.
 */
function Checkbox({ className, ...props }: Readonly<Omit<React.ComponentProps<'input'>, 'type'>>) {
  return (
    <input
      type="checkbox"
      data-slot="checkbox"
      className={cn('size-4 rounded-sm border border-input accent-primary', className)}
      {...props}
    />
  );
}

export { Checkbox };
